// src/components/hub/work/hooks/useWorkSubtasks.js
import { useCallback, useState } from "react";
import { createWork, getWorkSubtasks } from "../../../../api/hub/work";

// Owns subtask expansion state (which rows are expanded, the cached subtask
// lists, and the revision counter the slide-out's SubtaskList remounts on)
// plus every subtask handler. The work page keeps identical handler names, so
// TaskListView / TaskSlideOut props are unchanged.
const useWorkSubtasks = ({ patchItem, setBucketCache, setRetryNonce, toastError }) => {
  const [expandedSubtasks, setExpandedSubtasks] = useState(new Set());
  const [subtaskCache, setSubtaskCache] = useState({});
  const [subtaskRevision, setSubtaskRevision] = useState(0);

  // ─── Subtask expansion ──────────────────────────────────────────────────────
  const handleToggleSubtasks = useCallback(async (taskId) => {
    if (!taskId) return;
    setExpandedSubtasks((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId); else next.add(taskId);
      return next;
    });
    setSubtaskCache((prev) => { if (prev[taskId]) return prev; return { ...prev, [taskId]: null }; });
    try {
      const resp = await getWorkSubtasks(taskId);
      setSubtaskCache((prev) => ({ ...prev, [taskId]: resp?.data || [] }));
    } catch (e) {
      setExpandedSubtasks((prev) => { const next = new Set(prev); next.delete(taskId); return next; });
      setSubtaskCache((prev) => {
        if (!(taskId in prev)) return prev;
        const next = { ...prev }; delete next[taskId]; return next;
      });
    }
  }, []);

  // ─── Quick-add a subtask ──────────────────────────────────────────────────
  // One merged handler for both the "Add step" quick-capture row (InboxRow) and
  // the slide-out's subtask creation. The backend (fn_work_create with
  // p_parent_task_id) inherits the parent's client_id and company, so only the
  // title needs to be sent. On success we invalidate the cached subtask list,
  // re-fetch eagerly when the parent is expanded, bump subtaskRevision so the
  // slide-out SubtaskList remounts, and bump retryNonce so the parent's
  // `subtasks` count aggregate refreshes.
  const handleAddSubtask = useCallback(async (parentTaskId, title) => {
    if (!parentTaskId || !title) return;
    try {
      const resp = await createWork({ title, parent_task_id: parentTaskId });
      if (resp?.data?.error) {
        toastError(`Add step failed: ${resp.data.message || "Unknown error"}`, parentTaskId, "subtask_change");
        return;
      }
      // Invalidate the cached subtask list so the next expand re-fetches.
      setSubtaskCache((prev) => ({ ...prev, [parentTaskId]: null }));
      // Re-fetch eagerly if the parent is currently expanded, so the new step
      // shows without a manual collapse/expand toggle.
      if (expandedSubtasks.has(parentTaskId)) {
        try {
          const fresh = await getWorkSubtasks(parentTaskId);
          setSubtaskCache((prev) => ({ ...prev, [parentTaskId]: fresh?.data || [] }));
        } catch (_) { /* leave the null sentinel; next toggle re-fetches */ }
      }
      // Bump subtaskRevision so SubtaskList remounts and re-fetches its data.
      setSubtaskRevision((r) => r + 1);
      // Bump retryNonce so the parent's subtask count aggregate refreshes.
      setRetryNonce((n) => n + 1);
    } catch (e) {
      toastError(`Add step failed: ${e?.message || "Unknown error"}`, parentTaskId, "subtask_change");
    }
  }, [expandedSubtasks, setRetryNonce, toastError]);

  // Subtask deleted from an InboxRow's expanded view (or from SubtaskList in the
  // slide-out). The deleting component already removed the subtask from its own
  // local array and called this callback with (parentId, subtaskId). Here we
  // keep the caches consistent: drop the subtask from subtaskCache[parentId],
  // decrement the parent's `subtasks` count on every bucketCache entry (so the
  // row badge updates immediately), and bump subtaskRevision so the slide-out
  // SubtaskList remounts and re-fetches. No retryNonce bump — the count
  // aggregate is corrected locally via patchItem.
  const handleDeleteSubtask = useCallback((parentId, subtaskId) => {
    if (!parentId || !subtaskId) return;
    // Remove the subtask from the cached list for this parent (if cached).
    setSubtaskCache((prev) => {
      const arr = prev[parentId];
      if (!Array.isArray(arr)) return prev;
      return { ...prev, [parentId]: arr.filter((s) => s.id !== subtaskId) };
    });
    // Decrement the parent's `subtasks` count across every bucket cache entry.
    setBucketCache((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => {
        if (!next[key]?.items) return;
        next[key] = {
          ...next[key],
          items: next[key].items.map((i) => {
            if (i.id !== parentId) return i;
            const cur = Number(i.subtasks) || 0;
            return { ...i, subtasks: Math.max(0, cur - 1) };
          }),
        };
      });
      return next;
    });
    // Bump subtaskRevision so the slide-out SubtaskList remounts + re-fetches.
    setSubtaskRevision((r) => r + 1);
  }, [setBucketCache]);

  // Subtask count changed in an InboxRow's expanded <SubtaskSection> (add or
  // delete). SubtaskSection owns its own fetch/add/rename/delete state and
  // signals the new array length here so the row badge updates immediately via
  // patchItem — no refetch needed. Forwarded to TaskListView → InboxRow.
  const handleSubtaskCountChange = useCallback((taskId, newCount) => {
    patchItem(taskId, { subtasks: newCount });
    setSubtaskRevision((r) => r + 1);
  }, [patchItem]);

  return {
    expandedSubtasks,
    subtaskCache,
    subtaskRevision,
    setSubtaskRevision,
    handleToggleSubtasks,
    handleAddSubtask,
    handleDeleteSubtask,
    handleSubtaskCountChange,
  };
};

export default useWorkSubtasks;
